// CATALOG-SYNC-138 — load identity maps. AS-hash map is audit-only.
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { fetchSameOriginJsonFile } from "./catalog-static-json-v2.js";
import { normalizeIdentifier } from "./product-identity-v2.js";

export type StoreSkuMapRow = {
  store_sku: string;
  source?: string;
  status?: "verified" | "candidate";
  locked?: boolean;
  role?: string;
};

export type StoreSkuMapFile = {
  schema?: string;
  role?: string;
  products?: Record<string, StoreSkuMapRow>;
};

export type PromStoreSkuRow = {
  store_sku: string;
  source_product_id?: string;
  source_field?: string;
  gtin?: string | null;
  status?: string;
};

export type PromStoreSkuMapFile = {
  schema?: string;
  products?: Record<string, PromStoreSkuRow>;
};

let asHashCache: Promise<StoreSkuMapFile> | null = null;
let promSkuCache: Promise<PromStoreSkuMapFile> | null = null;

function isAsHash(value: string): boolean {
  return /^AS[0-9A-Z]{8}$/i.test(value);
}

function isStoreSkuMapFile(json: unknown): json is StoreSkuMapFile {
  return Boolean(json && typeof json === "object" && (json as StoreSkuMapFile).products);
}

function isPromStoreSkuMapFile(json: unknown): json is PromStoreSkuMapFile {
  return Boolean(json && typeof json === "object" && (json as PromStoreSkuMapFile).products);
}

async function loadAsHashAuditMap(): Promise<StoreSkuMapFile> {
  if (!asHashCache) {
    asHashCache = (async () => {
      const file = await fetchSameOriginJsonFile("catalog-store-sku-map-v1.json", isStoreSkuMapFile);
      return file ?? { products: {} };
    })();
  }
  return asHashCache;
}

async function loadPromStoreSkuMap(): Promise<PromStoreSkuMapFile> {
  if (!promSkuCache) {
    promSkuCache = (async () => {
      const file = await fetchSameOriginJsonFile("catalog-prom-store-sku-v1.json", isPromStoreSkuMapFile);
      return file ?? { products: {} };
    })();
  }
  return promSkuCache;
}

/**
 * CATALOG-SYNC-138: never apply AS-hash policy values as customer-facing store_sku.
 * Prefer catalog ms (already patched) or Prom store-sku map.
 */
export function applyStoreSkuMapToProduct(
  product: CatalogV2Product,
  row: StoreSkuMapRow | null | undefined,
): CatalogV2Product {
  // Retain generated_internal_code on product for diagnostics only — never as merchantSku.
  const internal = row?.store_sku?.trim() || "";
  if (internal && isAsHash(internal)) {
    const existing = String(product.merchantSku ?? "").trim();
    if (existing && isAsHash(existing)) {
      return {
        ...product,
        merchantSku: null,
        sku: product.promProductId || product.id,
        storeSkuSource: "generated_internal_code_suppressed",
        storeSkuVerified: false,
        generatedInternalCode: internal,
      };
    }
    return {
      ...product,
      generatedInternalCode: internal,
      storeSkuSource:
        existing && !isAsHash(existing)
          ? product.storeSkuSource ?? "prom.sku"
          : product.storeSkuSource ?? "absent",
    };
  }
  return product;
}

export function applyPromStoreSkuToProduct(
  product: CatalogV2Product,
  row: PromStoreSkuRow | null | undefined,
): CatalogV2Product {
  const storeSku = row?.store_sku?.trim() || "";
  if (!storeSku || storeSku === product.id || isAsHash(storeSku)) return product;
  const existing = String(product.merchantSku ?? "").trim();
  if (existing && !isAsHash(existing) && existing !== storeSku) {
    return {
      ...product,
      storeSkuSource: "NEEDS_HUMAN_SKU_MAPPING",
      storeSkuVerified: true,
    };
  }
  return {
    ...product,
    merchantSku: storeSku,
    sku: storeSku,
    barcodeGtin: row?.gtin?.trim() || product.barcodeGtin,
    storeSkuSource: row?.source_field || "prom.sku",
    storeSkuVerified: row?.status === "verified",
  };
}

export async function enrichCatalogWithStoreSkuMap(
  products: CatalogV2Product[],
): Promise<CatalogV2Product[]> {
  const [asHashFile, promFile] = await Promise.all([loadAsHashAuditMap(), loadPromStoreSkuMap()]);
  const asMap = asHashFile.products ?? {};
  const promMap = promFile.products ?? {};
  return products.map((p) => {
    const withProm = applyPromStoreSkuToProduct(
      p,
      promMap[p.id] ?? promMap[normalizeIdentifier(p.id)],
    );
    return applyStoreSkuMapToProduct(withProm, asMap[p.id] ?? asMap[normalizeIdentifier(p.id)]);
  });
}

export function resetStoreSkuMapCacheForTests(): void {
  asHashCache = null;
  promSkuCache = null;
}
